// Vendor imports
const _ = require('lodash')
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { formatCount } = require('shared/string-utils')
const {
  TYPES: modelTypes,
  ARTICLE_SUBTYPES: articleSubtypes,
  ARTICLE_SUBTYPES_KEYS: articleSubTypesKeys
} = require('shared/model-types')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const { stopPropagation } = require('client-v2/common/utils/events')
const { usePropsCallback, usePropsEventCallback } = require('client-v2/common/hooks/callbacks')
const useLineClampFitStyle = require('client-v2/common/hooks/line-clamp')
const useSymbol = require('client-v2/common/hooks/symbol')
const { CircleButton } = require('client-v2/common/components/buttons')
const EmbeddedPlayer = require('client-v2/common/components/embedded-player')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { RecReasonRecord } = require('client-v2/common/containers/feed/records')
const ArticleMixButton = require('client-v2/common/containers/lightweight-engagement/components/article-mix-button')
const AddToCollectionModalAnchor = require('client-v2/common/containers/lightweight-engagement/containers/add-to-collection-modal/components/anchor-smart')
const articleModelActions = require('client-v2/common/models/article/actions')
const ArticleModelRecord = require('client-v2/common/models/article/record')
const PageContextRecord = require('client-v2/common/models/page-context/record')
const ViewportObserver = require('client-v2/common/utils/viewport-observer')

const bem = BemEntity('ArticleCardV2')

function ArticleCard(props) {
  // Prefer the subtype enum key in the stylesheet instead of the value
  const subtypeKey = articleSubTypesKeys[props.article.subtype]

  // Hooks
  const blockRef = React.useRef()
  const anchorId = useSymbol()
  const onCardClick = usePropsEventCallback(props, 'onCardClick')
  const { isFullyVisible, isPartiallyVisible } = useArticleCardViewportObserver({
    domRef: blockRef,
    onFullyVisible: usePropsCallback(props, 'onSeen')
  })

  return (
    tag('div', {
      ref: blockRef,
      className: classNames(
        props.className,
        bem.block({
          'has-count': props.isCountVisible && !!props.article.count,
          'has-rec-reason': !!props.recReason,
          subtype: subtypeKey
        })
      ),
      dataset: {
        urlId: props.article['url_id']
      }
    },
      tag('div', {
        className: bem.element('inner', {
          interactive: !!props.onCardClick
        }),
        onClick: onCardClick
      },
        tag(ArticleCardContent, {
          ...props,
          // Flags
          isFullyVisible,
          isPartiallyVisible
        }),
        tag(ArticleCardFooter, props),
        tag(ArticleCardCount, props),
        tag('div', {
          className: bem.element('bottom-actions')
        },
          tag(ArticleCardShareButton, props),
          tag(ArticleCardAddButton, {
            ...props,
            anchorId
          })
        )
      )
    )
  )
}

ArticleCard.propTypes = {
  // Flags
  isCountVisible: PropTypes.bool,
  isEmbedDisclosed: PropTypes.bool,
  isHrefsEnabled: PropTypes.bool,
  // Values
  article: PropTypes.shape(ArticleModelRecord.propTypes).isRequired,
  className: PropTypes.string,
  itemIndex: PropTypes.number,
  pageContext: PropTypes.shape(PageContextRecord.propTypes),
  recReason: PropTypes.shape(RecReasonRecord.propTypes),
  // Callbacks
  onAddClick: PropTypes.func,
  onCardClick: PropTypes.func,
  onCountClick: PropTypes.func,
  onDomainClick: PropTypes.func,
  onEmbedDisclose: PropTypes.func,
  onMoreClick: PropTypes.func,
  onPreviewClick: PropTypes.func,
  onSeen: PropTypes.func,
  onShareClick: PropTypes.func
}

ArticleCard.defaultProps = {
  isCountVisible: true,
  isEmbedDisclosed: false, // Set to `true` to disable the embed curtain
  isHrefsEnabled: true
}

module.exports = ArticleCard

// React hooks

function useArticleCardViewportObserver({ domRef, onFullyVisible }) {
  // ViewportObserver is only available in browsers
  if (typeof window === 'undefined') {
    return { isFullyVisible: false, isPartiallyVisible: false }
  }
  const [ isFullyVisible, setFullyVisible ] = React.useState(false)
  const [ isPartiallyVisible, setPartiallyVisible ] = React.useState(false)
  React.useEffect(
    () => {
      const el = domRef.current

      const fullyVisibleObserver = new ViewportObserver({
        intersectionObserverOptions: {
          threshold: 1.00
        },
        callback() {
          if (!isFullyVisible) {
            setFullyVisible(true)
            setPartiallyVisible(true)
            onFullyVisible()
          }
        }
      })
      const partiallyVisibleObserver = new ViewportObserver({
        intersectionObserverOptions: {
          threshold: 0.1
        },
        callback() {
          if (!isPartiallyVisible) {
            setPartiallyVisible(true)
          }
        }
      })

      if (!isFullyVisible) {
        fullyVisibleObserver.observe(el)
      }
      if (!isPartiallyVisible) {
        partiallyVisibleObserver.observe(el)
      }

      return () => {
        fullyVisibleObserver.unobserve(el)
        partiallyVisibleObserver.unobserve(el)
      }
    },
    []
  )
  return { isFullyVisible, isPartiallyVisible }
}

// BEM Block entity Element components

function ArticleCardContent(props) {
  switch (props.article.subtype) {
    case articleSubtypes.TEXT:
      return tag([
        tag(ArticleCardDetails, props)
      ])
    case articleSubtypes.VIDEO:
    case articleSubtypes.AUDIO:
      return tag([
        tag(ArticleCardEmbed, props),
        tag(ArticleCardDetails, props)
      ])
    case articleSubtypes.IMAGE:
    case articleSubtypes.TEXT_WITH_IMAGE:
    default:
      return tag([
        tag(ArticleCardImage, props),
        tag(ArticleCardDetails, props)
      ])
  }
}

function ArticleCardDetails(props) {
  return (
    tag('div', {
      className: bem.element('details')
    },
      tag('div', {
        className: bem.element('top-actions')
      },
        tag(ArticleCardDomain, props),
        tag(ArticleCardPreviewButton, props),
        tag(ArticleCardMoreButton, props)
      ),
      tag(ArticleCardTitle, props),
      tag(ArticleCardDescription, props)
    )
  )
}

function ArticleCardTitle(props) {
  const ref = React.useRef()
  const style = useLineClampFitStyle({
    disabled: !props.isPartiallyVisible,
    domRef: ref
  })
  return (
    tag('a', {
      ref,
      className: bem.element('title'),
      href: getArticleContentHref(props),
      rel: 'nofollow noopener',
      style,
      target: '_blank'
    },
      props.article.title
    )
  )
}

function ArticleCardDescription(props) {
  const ref = React.useRef()
  const style = useLineClampFitStyle({
    disabled: !props.isPartiallyVisible,
    domRef: ref
  })
  if (!props.article.description) {
    return null
  }
  return (
    tag('a', {
      ref,
      className: bem.element('description'),
      href: getArticleContentHref(props),
      rel: 'nofollow noopener',
      style,
      target: '_blank'
    },
      props.article.description
    )
  )
}

function ArticleCardDomain(props) {
  const onClick = usePropsEventCallback(props, 'onDomainClick')
  return (
    tag('a', {
      className: bem.element('domain'),
      href: getDomainSearchHref(props),
      onClick
    },
      props.article.hostname
    )
  )
}

function ArticleCardPreviewButton(props) {
  const onClick = usePropsEventCallback(props, 'onPreviewClick')
  if (!props.onPreviewClick) {
    return null
  }
  return (
    tag(CircleButton, {
      className: bem.element('preview-button'),
      href: getArticleProfileHref(props),
      title: 'Get more details about this post',
      onClick
    })
  )
}

function ArticleCardMoreButton(props) {
  const onClick = usePropsEventCallback(props, 'onMoreClick')
  if (props.article.hasMix || !props.onMoreClick) {
    return null
  }
  return (
    tag(CircleButton, {
      className: bem.element('more-button'),
      title: 'Report this content or block the source',
      onClick
    })
  )
}

function ArticleCardEmbed(props) {
  const onEmbedDisclose = usePropsCallback(props, 'onEmbedDisclose')
  const [ isDisclosed, setDisclosed ] = React.useState(props.isEmbedDisclosed)
  const onDisclose = React.useCallback(
    () => {
      if (!isDisclosed) {
        onEmbedDisclose()
        setDisclosed(true)
      }
    },
    [ isDisclosed, setDisclosed, onEmbedDisclose ]
  )
  const src = props.article.embedUrl
  if (!src || !props.isPartiallyVisible) {
    return tag(ArticleCardImage, props)
  }
  return (
    tag('div', {
      className: bem.element('embed-container')
    },
      tag(ArticleCardImage, props),
      tag(EmbeddedPlayer, {
        className: bem.element('embed'),
        isDisclosed,
        src,
        onDisclose
      })
    )
  )
}

function ArticleCardImage(props) {
  const {
    article: { image, md5image }
  } = props
  return (
    tag(LazyImage, {
      className: bem.element('image'),
      href: getArticleContentHref(props),
      rel: 'nofollow noopener',
      target: '_blank',
      sources: [ md5image, image ]
    })
  )
}

function ArticleCardAddButton(props) {
  const onClick = usePropsEventCallback(props, 'onAddClick')
  if (!props.onAddClick) {
    return null
  }
  return (
    tag(AddToCollectionModalAnchor, {
      anchorId: props.anchorId,
      popperClassName: bem.element('add-button-popper'),
      urlId: props.article['url_id']
    },
      tag(ArticleMixButton, {
        className: bem.element('add-button'),
        checked: props.article.hasMix,
        onClick
      })
    )
  )
}

function ArticleCardShareButton(props) {
  const onClick = usePropsEventCallback(props, 'onShareClick')
  if (!props.onShareClick || !props.article.hasMix) {
    return null
  }
  return (
    tag(CircleButton, {
      className: bem.element('share-button'),
      title: 'Share this post with a friend',
      onClick
    })
  )
}

function ArticleCardFooter(props) {
  const recReasonHtml = _.get(props, ['recReason', 'html'])
  if (!recReasonHtml) {
    return (
      tag('div', {
        className: bem.element('footer')
      })
    )
  }
  return (
    tag('div', {
      className: bem.element('footer'),
      onClick: stopPropagation
    },
      tag(ArticleCardFooterIcon, props),
      tag('span', {
        className: bem.element('footer-text'),
        dangerouslySetInnerHTML: { __html: recReasonHtml }
      })
    )
  )
}

function ArticleCardFooterIcon(props) {
  const firstRecReasonItem = _.get(props, ['recReason', 'items', 0])
  if (firstRecReasonItem && firstRecReasonItem.type === modelTypes.USER) {
    return (
      tag(UserAvatar, {
        className: bem.element('footer-icon'),
        href: true,
        user: firstRecReasonItem
      })
    )
  }
  return null
}

function ArticleCardCount(props) {
  const onClick = usePropsEventCallback(props, 'onCountClick')
  if (!props.isCountVisible || !props.article.count) {
    return null
  }
  return (
    tag('a', {
      className: bem.element('count'),
      href: getArticleProfileHref(props),
      onClick
    },
      `${formatCount(props.article.count)}`
    )
  )
}

// Linkage helpers

function getArticleContentHref(props) {
  if (props.isHrefsEnabled) {
    return props.article.url
  }
}

function getDomainSearchHref(props) {
  if (props.isHrefsEnabled) {
    return articleModelActions.articleDomainSearchHref(props.article)
  }
}

function getArticleProfileHref(props) {
  if (props.isHrefsEnabled) {
    return articleModelActions.articleProfileHref(props.article)
  }
}
